// Centralized API client with automatic token expiration handling
import { API_BASE } from '../services/authService';

/**
 * Wrapper around fetch that automatically handles token expiration
 * When a 401 (Unauthorized) response is received, it clears the token
 * and redirects to the login page
 */
export async function apiFetch(url, options = {}) {
  // Get token from local storage
  const token = localStorage.getItem('userToken');
  
  // Add Authorization header if token exists
  const headers = {
    ...options.headers,
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };

  // Construct the full URL
  const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;

  try {
    const response = await fetch(fullUrl, {
      ...options,
      headers,
      credentials: 'include',
    });

    // Check if token has expired (401 Unauthorized)
    if (response.status === 401) {
      console.warn('Token expired or unauthorized. Redirecting to login...');
      
      // Clear all authentication data
      localStorage.removeItem('userToken');
      localStorage.removeItem('user');
      localStorage.removeItem('employee');
      
      // Redirect to login page
      // Using window.location instead of navigate since this might be called from anywhere
      window.location.href = '/';
      
      // Return a rejected promise to prevent further processing
      return Promise.reject(new Error('Unauthorized - Token expired'));
    }

    // Return the response for normal handling
    return response;
  } catch (error) {
    console.error('API fetch error:', error);
    throw error;
  }
}

/**
 * Helper function to get JSON from API with automatic error handling
 */
export async function apiGet(url, options = {}) {
  const response = await apiFetch(url, { ...options, method: 'GET' });
  
  if (!response.ok) {
    throw new Error(`API request failed: ${response.status} ${response.statusText}`);
  }
  
  return await response.json();
}

/**
 * Helper function to post data to API with automatic error handling
 */
export async function apiPost(url, data, options = {}) {
  const response = await apiFetch(url, {
    ...options,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    body: JSON.stringify(data),
  });
  
  if (!response.ok) {
    throw new Error(`API request failed: ${response.status} ${response.statusText}`);
  }
  
  return await response.json();
}

/**
 * Helper function to upload files to API with automatic error handling
 */
export async function apiUpload(url, formData, options = {}) {
  const token = localStorage.getItem('userToken');
  
  const headers = {
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;

  const response = await fetch(fullUrl, {
    ...options,
    method: 'POST',
    headers,
    body: formData,
    credentials: 'include',
  });

  // Check if token has expired (401 Unauthorized)
  if (response.status === 401) {
    console.warn('Token expired or unauthorized. Redirecting to login...');
    
    // Clear all authentication data
    localStorage.removeItem('userToken');
    localStorage.removeItem('user');
    localStorage.removeItem('employee');
    
    // Redirect to login page
    window.location.href = '/';
    
    return Promise.reject(new Error('Unauthorized - Token expired'));
  }
  
  if (!response.ok) {
    throw new Error(`API request failed: ${response.status} ${response.statusText}`);
  }
  
  return await response.json();
}

